/**
 * The `room-link/1` messages (plan 2026-10-21-plan-controller-session-link S1, S13–S23), independent of the
 * transport that carries them (D20).
 *
 * The link is defined as MESSAGES on two logical channels — reliable ordered JSON, and unreliable binary frames
 * — never as WebRTC data channels. Nothing here names a data channel, an SDP or an ICE state: a transport
 * (today `webrtc-turn`, `roomLinkWebRtc.ts`) decides how a message travels, and this module decides what it is.
 */

/** S1: the protocol id, in the ticket, every hello*, every channel and the device's capability. */
export const ROOM_LINK_PROTOCOL = 'room-link/1';

/** S22: a JSON message larger than this many bytes travels as `chunk` fragments of at most this many. */
export const ROOM_LINK_CHUNK_BYTES = 60_000;

/** S22: the largest message a receiver reassembles. */
export const ROOM_LINK_MAX_ASSEMBLED_BYTES = 4 * 1024 * 1024;

/** S22: the most fragments one message may take (a 4 MiB message of escapes needs up to 420). */
export const ROOM_LINK_MAX_CHUNK_PARTS = 420;

/** S23: why a link ends, on either side. */
export type RoomLinkEndReason =
  | 'client_closed' | 'lease_expired' | 'link_dead' | 'membership_revoked' | 'role_changed' | 'device_revoked'
  | 'link_capacity' | 'connect_timeout' | 'transport_policy' | 'transport_closed' | 'controller_restart';

export const ROOM_LINK_END_REASONS: ReadonlySet<string> = new Set<RoomLinkEndReason>([
  'client_closed', 'lease_expired', 'link_dead', 'membership_revoked', 'role_changed', 'device_revoked',
  'link_capacity', 'connect_timeout', 'transport_policy', 'transport_closed', 'controller_restart',
]);

export type RoomLinkInteraction = 'editing' | 'committed';
export type RoomLinkLiveViewDuration = 'default' | 'extended';

/** S15: one command, the queue child's own envelope. It carries no actor: the controller stamps it from the ticket. */
export interface RoomLinkCommandMessage {
  type: 'command';
  cmdId: string;
  envelope: { name: string; paramsJson: string };
  coalesceKey: string | null;
  interaction: RoomLinkInteraction | null;
}

/** S16: the controller's answer to one command (QueueAck semantics). */
export interface RoomLinkResultMessage {
  type: 'result';
  cmdId: string;
  status: 'done' | 'rejected';
  authoredRevision?: number;
  resultJson?: string;
  error?: { code: string; message: string };
}

/** S17: one keep-last drag sample. */
export interface RoomLinkIntentMessage {
  type: 'intent'; rigId: string; controlKey: string; command: string; paramsJson: string;
}

/** S18: who is working the room, for the controller to record and fan out. */
export interface RoomLinkAnnounceMessage {
  type: 'announce'; command: string; coalesceKey: string; interaction: RoomLinkInteraction; paramsJson: string | null;
}

/** S19: the relay's `activity` and `lastCommand` shapes, from the controller. */
export interface RoomLinkCollabMessage {
  type: 'collab'; activity?: Record<string, unknown>; lastCommand?: Record<string, unknown>;
}

/** S20: one authored node, byte-identical to the cloud copy at `controllerAuthored/{deviceId}/{path}`. */
export interface RoomLinkNodeMessage { type: 'node'; path: string; node: unknown }
export interface RoomLinkSnapshotDoneMessage { type: 'snapshotDone'; revision: number; epoch: string }

/** S21: the controller's answers to a live view request. */
export interface RoomLinkLiveViewAck {
  type: 'liveViewAck'; duration: RoomLinkLiveViewDuration; endsInMs: number | null; ceilingInMs: number;
}
/** `since` is when the holder's view started: the controller's wall clock, Unix epoch milliseconds. */
export interface RoomLinkLiveViewHolder { uid: string; displayName: string; since: number }
export interface RoomLinkLiveViewBusy { type: 'liveViewBusy'; holder: RoomLinkLiveViewHolder }
export interface RoomLinkLiveViewEnd { type: 'liveViewEnd'; reason: string; byDisplayName?: string }

/** Every message the console sends. */
export type RoomLinkOutbound =
  | { type: 'hello'; version: typeof ROOM_LINK_PROTOCOL; linkId: string; ticket: string; clientTimeUs: string }
  | { type: 'helloProof'; version: typeof ROOM_LINK_PROTOCOL; linkId: string; signature: string; clientReceiveTimeUs: string;
    clockOffsetUs: string; rttUs: string }
  | { type: 'ping'; seq: number }
  | { type: 'pong'; seq: number }
  | RoomLinkCommandMessage
  | RoomLinkIntentMessage
  | RoomLinkAnnounceMessage
  | { type: 'subscribe'; domains: string[]; fixtures: boolean }
  | { type: 'liveView'; action: 'start' | 'takeOver' | 'setDuration'; duration: RoomLinkLiveViewDuration }
  | { type: 'liveView'; action: 'stop' }
  | { type: 'ready'; mapDigest: string }
  | { type: 'end'; reason: RoomLinkEndReason };

/** A message as received: a JSON object with a string `type`, not yet checked against its shape. */
export interface RoomLinkInbound { type: string; [key: string]: unknown }

/** The messages that travel with the room's state and the live view's bulk, rather than with commands (D5). */
export const ROOM_LINK_STATE_MESSAGES: ReadonlySet<string> = new Set(['subscribe', 'ready']);

/**
 * Split `text` into slices whose JSON string encoding, quotes included, is at most `limit` bytes, never inside a
 * code point.
 * Bounding the ENCODED slice (quotes and control characters grow when escaped) keeps every chunk message
 * under the receiver's 65,536-byte message limit, and the raw slice under S22's 60,000 bytes with it.
 */
function encodedBytes(codePoint: number): number {
  if (codePoint === 0x22 || codePoint === 0x5c || codePoint === 0x08 || codePoint === 0x0c || codePoint === 0x0a
    || codePoint === 0x0d || codePoint === 0x09) return 2;
  if (codePoint < 0x20 || (codePoint >= 0xd800 && codePoint <= 0xdfff)) return 6;
  if (codePoint < 0x80) return 1;
  if (codePoint < 0x800) return 2;
  return codePoint < 0x10000 ? 3 : 4;
}

function slices(text: string, limit: number): string[] {
  const parts: string[] = [];
  let current = '';
  let size = 2;
  for (const character of text) {
    const bytes = encodedBytes(character.codePointAt(0)!);
    if (size + bytes > limit && current) {
      parts.push(current);
      current = '';
      size = 2;
    }
    current += character;
    size += bytes;
  }
  if (current) parts.push(current);
  return parts;
}

/**
 * One message as the text frames that carry it: the JSON itself, or, above 60,000 bytes, `chunk` fragments
 * `{type:'chunk', id, part, parts, data}` in order (S22). `nextId` numbers the fragmented messages of one channel.
 */
export function encodeRoomLinkMessage(message: object, nextId: () => string): string[] {
  return encodeRoomLinkText(JSON.stringify(message), nextId);
}

/** As `encodeRoomLinkMessage`, for a message already serialized (the golden vectors carry the text). */
export function encodeRoomLinkText(text: string, nextId: () => string): string[] {
  if (new TextEncoder().encode(text).length <= ROOM_LINK_CHUNK_BYTES) return [text];
  const data = slices(text, ROOM_LINK_CHUNK_BYTES);
  const id = nextId();
  return data.map((slice, part) => JSON.stringify({ type: 'chunk', id, part, parts: data.length, data: slice }));
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The receiving half of S22 for one channel: whole messages pass straight through, fragments are held by `id`
 * until every part is in. Returns the parsed message, or undefined while a fragmented one is incomplete.
 * Throws on anything that is not a message or a well-formed fragment; the link ends on that.
 */
export class RoomLinkChunkAssembler {
  private readonly partial = new Map<string, { parts: number; data: Array<string | undefined>; received: number; bytes: number }>();

  add(raw: string): RoomLinkInbound | undefined {
    const parsed = JSON.parse(raw) as unknown;
    if (!isObject(parsed) || typeof parsed.type !== 'string') throw new Error('room link: a message has no type');
    if (parsed.type !== 'chunk') return parsed as RoomLinkInbound;
    const { id, part, parts, data } = parsed;
    if (typeof id !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(id) || !Number.isInteger(parts) || (parts as number) < 2 || (parts as number) > ROOM_LINK_MAX_CHUNK_PARTS
      || !Number.isInteger(part) || (part as number) < 0 || (part as number) >= (parts as number) || typeof data !== 'string') {
      throw new Error('room link: a malformed chunk');
    }
    const key = id;
    const entry = this.partial.get(key)
      ?? { parts: parts as number, data: Array.from<string | undefined>({ length: parts as number }), received: 0, bytes: 0 };
    if (entry.parts !== parts || entry.data[part as number] !== undefined) throw new Error('room link: a chunk disagrees with its message');
    entry.data[part as number] = data;
    entry.received += 1;
    entry.bytes += new TextEncoder().encode(data).length;
    if (entry.bytes > ROOM_LINK_MAX_ASSEMBLED_BYTES) throw new Error('room link: a message over 4 MiB');
    this.partial.set(key, entry);
    if (entry.received < entry.parts) return undefined;
    this.partial.delete(key);
    return this.add(entry.data.join(''));
  }
}

/** The ticket claims the console reads (S10): the nonce its proof names, and the lifetime its renewal keys on. */
export interface RoomLinkTicketClaims { nonce: string; issuedAt: number; expiresAt: number }

export function roomLinkTicketClaims(ticket: string): RoomLinkTicketClaims {
  const part = ticket.split('.')[1];
  if (!part) throw new Error('room link: the ticket is not a compact JWS');
  const padded = part.replace(/-/g, '+').replace(/_/g, '/');
  const claims = JSON.parse(atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))) as Record<string, unknown>;
  if (typeof claims.nonce !== 'string' || !Number.isSafeInteger(claims.issuedAt) || !Number.isSafeInteger(claims.expiresAt)
    || (claims.expiresAt as number) <= (claims.issuedAt as number)) {
    throw new Error('room link: the ticket carries no nonce or lifetime');
  }
  return { nonce: claims.nonce, issuedAt: claims.issuedAt as number, expiresAt: claims.expiresAt as number };
}

/**
 * S13: the text the console signs with its ephemeral key. The output-view proof's lines, under this protocol's
 * heading and with `linkId=` in place of `sessionId=`, joined by LF with no trailing LF.
 */
export function roomLinkHelloProofText(fields: {
  linkId: string; nonce: string; deviceChallenge: string; clientTimeUs: bigint; deviceReceiveTimeUs: bigint;
  deviceSendTimeUs: bigint; clientReceiveTimeUs: bigint; clockOffsetUs: bigint; rttUs: bigint;
}): string {
  return [
    `${ROOM_LINK_PROTOCOL} hello-proof`, `linkId=${fields.linkId}`, `nonce=${fields.nonce}`,
    `deviceChallenge=${fields.deviceChallenge}`, `clientTimeUs=${fields.clientTimeUs}`,
    `deviceReceiveTimeUs=${fields.deviceReceiveTimeUs}`, `deviceSendTimeUs=${fields.deviceSendTimeUs}`,
    `clientReceiveTimeUs=${fields.clientReceiveTimeUs}`, `clockOffsetUs=${fields.clockOffsetUs}`, `rttUs=${fields.rttUs}`,
  ].join('\n');
}

const MICROS = /^(0|[1-9][0-9]{0,19})$/;

/** S13: a helloChallenge for this link, echoing the hello's time; undefined for anything else. */
export function parseHelloChallenge(message: RoomLinkInbound, linkId: string, clientTimeUs: bigint):
  { deviceChallenge: string; deviceReceiveTimeUs: bigint; deviceSendTimeUs: bigint } | undefined {
  if (message.version !== ROOM_LINK_PROTOCOL || message.linkId !== linkId || message.clientTimeUs !== String(clientTimeUs)
    || typeof message.deviceChallenge !== 'string' || !/^[A-Za-z0-9_-]{43}$/.test(message.deviceChallenge)
    || typeof message.deviceReceiveTimeUs !== 'string' || !MICROS.test(message.deviceReceiveTimeUs)
    || typeof message.deviceSendTimeUs !== 'string' || !MICROS.test(message.deviceSendTimeUs)) return undefined;
  return { deviceChallenge: message.deviceChallenge, deviceReceiveTimeUs: BigInt(message.deviceReceiveTimeUs),
    deviceSendTimeUs: BigInt(message.deviceSendTimeUs) };
}

/** S13: a helloAck for this link; undefined for anything else. */
export function parseHelloAck(message: RoomLinkInbound, linkId: string):
  { clockOffsetUs: bigint; rttUs: bigint; revision: number; epoch: string; maxMessageBytes: number } | undefined {
  if (message.version !== ROOM_LINK_PROTOCOL || message.linkId !== linkId
    || typeof message.clockOffsetUs !== 'string' || !/^-?(0|[1-9][0-9]{0,19})$/.test(message.clockOffsetUs)
    || typeof message.rttUs !== 'string' || !MICROS.test(message.rttUs)
    || !Number.isSafeInteger(message.revision) || (message.revision as number) < 0 || typeof message.epoch !== 'string'
    || !Number.isSafeInteger(message.maxMessageBytes) || (message.maxMessageBytes as number) <= 0) return undefined;
  return { clockOffsetUs: BigInt(message.clockOffsetUs), rttUs: BigInt(message.rttUs), revision: message.revision as number,
    epoch: message.epoch, maxMessageBytes: message.maxMessageBytes as number };
}

/** S16: a result, or undefined when the shape is not one. */
export function parseResult(message: RoomLinkInbound): RoomLinkResultMessage | undefined {
  if (typeof message.cmdId !== 'string' || (message.status !== 'done' && message.status !== 'rejected')) return undefined;
  if (message.authoredRevision !== undefined && (!Number.isSafeInteger(message.authoredRevision) || (message.authoredRevision as number) < 0)) return undefined;
  if (message.resultJson !== undefined && typeof message.resultJson !== 'string') return undefined;
  const error = message.error;
  if (error !== undefined && (!isObject(error) || typeof error.code !== 'string' || typeof error.message !== 'string')) return undefined;
  return message as unknown as RoomLinkResultMessage;
}

/** S21: a liveViewAck, or undefined. */
export function parseLiveViewAck(message: RoomLinkInbound): RoomLinkLiveViewAck | undefined {
  if ((message.duration !== 'default' && message.duration !== 'extended')
    || (message.endsInMs !== null && (typeof message.endsInMs !== 'number' || !Number.isFinite(message.endsInMs) || message.endsInMs < 0))
    || typeof message.ceilingInMs !== 'number' || !Number.isFinite(message.ceilingInMs) || message.ceilingInMs < 0) return undefined;
  return message as unknown as RoomLinkLiveViewAck;
}

/** S21: a liveViewBusy with a named holder, or undefined. */
export function parseLiveViewBusy(message: RoomLinkInbound): RoomLinkLiveViewBusy | undefined {
  const holder = message.holder;
  if (!isObject(holder) || typeof holder.uid !== 'string' || typeof holder.displayName !== 'string' || !holder.displayName
    || !Number.isSafeInteger(holder.since) || (holder.since as number) < 0) return undefined;
  return message as unknown as RoomLinkLiveViewBusy;
}
